import { useMemo, useState } from "react";

import { useNavigate, useParams } from "react-router-dom";

import dayjs from "dayjs";
import {
  ArrowLeft,
  BookOpen,
  Building2,
  CheckCircle,
  Download,
  FileText,
  Info,
  Lock,
  Pencil,
  Plus,
  Printer,
  Trash2,
  Undo2,
  XCircle,
} from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { SearchableSelect } from "~/components/ui/searchable-select";
import { Skeleton } from "~/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { useAccounts } from "~/hooks/contabilidad/useAccounts";
import {
  useCreateMoveLine,
  useDeleteMoveLine,
  useMoveLines,
  useMovement,
  usePublishMovement,
  useUnpublishMovement,
  useUpdateMoveLine,
  useUpdateMovement,
} from "~/hooks/contabilidad/useMovements";
import { useReturnSupplierPaymentDraft } from "~/hooks/purchasing/useSupplierPayments";
import { exportMovimientoToExcel } from "~/services/contabilidad/movimientoExportService";
import { useProfileStore } from "~/store/profileStore";
import { generateComprobantePdf } from "~/utils/comprobantePdf";

import { MovimientoDialog } from "./components/MovimientoDialog";
import { isSupplierPaymentDraft as isSupplierPaymentDraftMovement } from "./utils/supplierPaymentDraft";

import type {
  AccountMoveLine,
  MoveLineFormData,
  MovementFormData,
} from "~/types/contabilidad";

export default function MovimientoDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const role = useProfileStore((state) => state.role);
  const isAdmin = role === "superadmin" || role === "admin";
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [unpublishDialogOpen, setUnpublishDialogOpen] = useState(false);
  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [editingLine, setEditingLine] = useState<AccountMoveLine | null>(null);
  const [newLine, setNewLine] = useState<Partial<MoveLineFormData> | null>(
    null
  );

  const { data: movement, isLoading } = useMovement(id!);
  const { data: lines = [], isLoading: linesLoading } = useMoveLines(id!);
  const { data: accounts = [] } = useAccounts({ is_active: true });

  // On a pago-a-proveedor draft the AP (Proveedores, código 2103·) lines are owned
  // by the subledger: they stay locked and the comprobante is edited preserving them.
  const isSupplierPaymentDraft =
    !!movement && isSupplierPaymentDraftMovement(movement);
  const updateMutation = useUpdateMovement();
  const publishMutation = usePublishMovement();
  const unpublishMutation = useUnpublishMovement();
  const returnToSupplierPaymentsMutation = useReturnSupplierPaymentDraft();
  const createLineMutation = useCreateMoveLine();
  const updateLineMutation = useUpdateMoveLine();
  const deleteLineMutation = useDeleteMoveLine();

  // Filter accounts by company when movement is loaded
  const filteredAccounts = movement
    ? accounts.filter((a) => a.company_id === movement.company_id)
    : [];

  // Options for the searchable account picker (search by code or name).
  const accountOptions = useMemo(
    () =>
      filteredAccounts.map((account) => ({
        value: account.id,
        label: `${account.code} - ${account.name}`,
        searchTerms: `${account.code} ${account.name}`,
      })),
    [filteredAccounts]
  );

  const handleUpdate = (data: MovementFormData) => {
    updateMutation.mutate(
      { id: id!, data, preserveApLines: isSupplierPaymentDraft },
      {
        onSuccess: () => {
          setEditDialogOpen(false);
        },
      }
    );
  };

  const handlePublish = () => {
    publishMutation.mutate(id!, {
      onSuccess: () => {
        setPublishDialogOpen(false);
        // Return to the comprobantes list, which restores the filters the user
        // had active (status card + date range) from the session.
        navigate("/contabilidad/movimientos");
      },
    });
  };

  const handleUnpublish = () => {
    unpublishMutation.mutate(id!, {
      onSuccess: () => {
        setUnpublishDialogOpen(false);
      },
    });
  };

  const handleReturnToSupplierPayments = () => {
    returnToSupplierPaymentsMutation.mutate(id!, {
      onSuccess: () => {
        setReturnDialogOpen(false);
        navigate("/contabilidad/movimientos");
      },
    });
  };

  const handleAddLine = () => {
    setNewLine({
      account_id: "",
      description: "",
      debit: 0,
      credit: 0,
    });
  };

  const handleSaveNewLine = () => {
    if (newLine && newLine.account_id) {
      createLineMutation.mutate(
        {
          move_id: id!,
          account_id: newLine.account_id,
          description: newLine.description || null,
          debit: newLine.debit || 0,
          credit: newLine.credit || 0,
        },
        {
          onSuccess: () => {
            setNewLine(null);
          },
        }
      );
    }
  };

  const handleCancelNewLine = () => {
    setNewLine(null);
  };

  const handleEditLine = (line: AccountMoveLine) => {
    setEditingLine(line);
  };

  const handleSaveEditLine = () => {
    if (editingLine) {
      updateLineMutation.mutate(
        {
          id: editingLine.id,
          data: {
            account_id: editingLine.account_id,
            description: editingLine.description || null,
            debit: editingLine.debit,
            credit: editingLine.credit,
          },
        },
        {
          onSuccess: () => {
            setEditingLine(null);
          },
        }
      );
    }
  };

  const handleCancelEditLine = () => {
    setEditingLine(null);
  };

  const handleDeleteLine = (lineId: string) => {
    deleteLineMutation.mutate({ id: lineId, moveId: id! });
  };

  const handleExportExcel = () => {
    if (movement) {
      exportMovimientoToExcel({ movement, lines });
    }
  };

  const handlePrint = () => {
    if (movement) {
      generateComprobantePdf({ movement, lines });
    }
  };

  // Calculate totals
  const totalDebit = lines.reduce((sum, line) => sum + Number(line.debit), 0);
  const totalCredit = lines.reduce((sum, line) => sum + Number(line.credit), 0);
  const isBalanced = Math.abs(totalDebit - totalCredit) < 0.01;

  if (isLoading) {
    return (
      <div className="container mx-auto w-[80vw] space-y-6 py-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!movement) {
    return (
      <div className="container mx-auto w-[80vw] py-6">
        <p className="text-muted-foreground">Comprobante no encontrado.</p>
        <Button
          variant="link"
          onClick={() => navigate("/contabilidad/movimientos")}
          className="mt-4"
        >
          Volver a la lista
        </Button>
      </div>
    );
  }

  // On a pago-a-proveedor asiento the AP (Proveedores) lines are owned by the subledger: editing them
  // would diverge supplier_invoices.saldo_clp from the ledger (the DB guard blocks it), so they stay
  // locked and are corrected via "Devolver a Pago Proveedores". The rest of the lines (honorarios,
  // gastos, anticipos, banco) are free to edit — Tesorería arma la nómina del banco con ellas.
  const showLineActions = !movement.is_published;
  // AP control lines (Proveedores, código 2103·) are the locked ones on a supplier-payment draft.
  const isApLine = (line: AccountMoveLine) =>
    isSupplierPaymentDraft &&
    (line.account?.code ?? "").replace(/\D/g, "").startsWith("2103");

  return (
    <div className="container mx-auto w-[80vw] space-y-6 py-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/contabilidad/movimientos")}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-bold">
              <FileText className="h-6 w-6" />
              <span className="font-mono">{movement.move_number}</span>
              <span className="text-muted-foreground">•</span>
              {movement.ref}
            </h1>
            <p className="text-muted-foreground">
              {dayjs(movement.move_date).format("DD/MM/YYYY")} •{" "}
              {movement.journal?.code} - {movement.journal?.name}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleExportExcel}
            className="rounded-full"
            disabled={lines.length === 0}
          >
            <Download className="mr-2 h-4 w-4" />
            Descargar Excel
          </Button>
          {movement.is_published && (
            <Button
              variant="outline"
              onClick={handlePrint}
              className="rounded-full"
            >
              <Printer className="mr-2 h-4 w-4" />
              Imprimir
            </Button>
          )}
          {!movement.is_published ? (
            <>
              {isSupplierPaymentDraft && (
                <Button
                  variant="outline"
                  onClick={() => setReturnDialogOpen(true)}
                  className="rounded-full"
                >
                  <Undo2 className="mr-2 h-4 w-4" />
                  Devolver a Pago Proveedores
                </Button>
              )}
              {/* On a supplier-payment draft the AP lines are locked in the editor,
                  so Editar changes the header (fecha + referencia) and the additional
                  non-AP lines. Other drafts edit the full comprobante. */}
              <Button
                variant="outline"
                onClick={() => setEditDialogOpen(true)}
                className="rounded-full"
              >
                <Pencil className="mr-2 h-4 w-4" />
                Editar
              </Button>
              <Button
                onClick={() => setPublishDialogOpen(true)}
                className="rounded-full"
                disabled={!isBalanced || lines.length === 0}
              >
                <CheckCircle className="mr-2 h-4 w-4" />
                Publicar
              </Button>
            </>
          ) : (
            isAdmin && (
              <Button
                variant="outline"
                onClick={() => setUnpublishDialogOpen(true)}
                className="rounded-full"
              >
                <XCircle className="mr-2 h-4 w-4" />
                Despublicar
              </Button>
            )
          )}
        </div>
      </div>

      {isSupplierPaymentDraft && (
        <div className="flex items-start gap-2 rounded-lg border bg-muted/40 px-4 py-3 text-sm text-muted-foreground">
          <Info className="mt-0.5 h-4 w-4 shrink-0" />
          <p>
            Las líneas de{" "}
            <span className="font-medium text-foreground">Proveedores</span>{" "}
            provienen del pago y están bloqueadas: para corregir un monto usa{" "}
            <span className="font-medium text-foreground">
              Devolver a Pago Proveedores
            </span>{" "}
            y vuelve a registrar el pago. Las demás líneas (honorarios, gastos,
            anticipos, banco) sí se pueden agregar y editar aquí.
          </p>
        </div>
      )}

      {/* Info Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Estado</CardTitle>
            {movement.is_published ? (
              <CheckCircle className="h-4 w-4 text-green-500" />
            ) : (
              <FileText className="h-4 w-4 text-yellow-500" />
            )}
          </CardHeader>
          <CardContent>
            <Badge
              variant={movement.is_published ? "default" : "secondary"}
              className={
                movement.is_published
                  ? "bg-green-100 text-green-800"
                  : "bg-yellow-100 text-yellow-800"
              }
            >
              {movement.is_published ? "Publicado" : "Borrador"}
            </Badge>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Diario</CardTitle>
            <BookOpen className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <p
              className="cursor-pointer font-medium text-primary hover:underline"
              onClick={() =>
                navigate(`/contabilidad/diarios/${movement.journal_id}`)
              }
            >
              {movement.journal?.code} - {movement.journal?.name}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Empresa</CardTitle>
            <Building2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <p
              className="cursor-pointer font-medium text-primary hover:underline"
              onClick={() =>
                navigate(`/contabilidad/empresas/${movement.company_id}`)
              }
            >
              {movement.company?.name}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Balance</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <Badge variant={isBalanced ? "default" : "destructive"}>
              {isBalanced ? "Cuadrado" : "Descuadrado"}
            </Badge>
          </CardContent>
        </Card>
      </div>

      {/* Lines Table */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Líneas del Comprobante</CardTitle>
          {showLineActions && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleAddLine}
              disabled={!!newLine}
              className="rounded-full"
            >
              <Plus className="mr-2 h-4 w-4" />
              Agregar Línea
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {linesLoading ? (
            <div className="space-y-2">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : (
            <div className="rounded-xl border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[250px]">Cuenta</TableHead>
                    <TableHead>Descripción</TableHead>
                    <TableHead className="w-[150px] text-right">
                      Débito
                    </TableHead>
                    <TableHead className="w-[150px] text-right">
                      Crédito
                    </TableHead>
                    {showLineActions && (
                      <TableHead className="w-[100px]">Acciones</TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lines.map((line, index) =>
                    editingLine?.id === line.id ? (
                      <TableRow key={line.id}>
                        <TableCell>
                          <SearchableSelect
                            value={editingLine.account_id}
                            onValueChange={(value) =>
                              setEditingLine({
                                ...editingLine,
                                account_id: value,
                              })
                            }
                            options={accountOptions}
                            placeholder="Seleccionar cuenta"
                            emptyMessage="Sin cuentas"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            value={editingLine.description || ""}
                            onChange={(e) =>
                              setEditingLine({
                                ...editingLine,
                                description: e.target.value,
                              })
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={editingLine.debit}
                            onChange={(e) =>
                              setEditingLine({
                                ...editingLine,
                                debit: parseFloat(e.target.value) || 0,
                              })
                            }
                            className="text-right"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={editingLine.credit}
                            onChange={(e) =>
                              setEditingLine({
                                ...editingLine,
                                credit: parseFloat(e.target.value) || 0,
                              })
                            }
                            className="text-right"
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={handleSaveEditLine}
                            >
                              <CheckCircle className="h-4 w-4 text-green-500" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={handleCancelEditLine}
                            >
                              <XCircle className="h-4 w-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      <TableRow
                        key={line.id}
                        className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
                        style={{
                          animationDelay: `${Math.min(index * 40, 400)}ms`,
                        }}
                      >
                        <TableCell className="font-medium">
                          {line.account?.code} - {line.account?.name}
                        </TableCell>
                        <TableCell>{line.description || "-"}</TableCell>
                        <TableCell className="text-right font-mono">
                          {Number(line.debit).toLocaleString("es-CL", {
                            minimumFractionDigits: 2,
                          })}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {Number(line.credit).toLocaleString("es-CL", {
                            minimumFractionDigits: 2,
                          })}
                        </TableCell>
                        {showLineActions && (
                          <TableCell>
                            {isApLine(line) ? (
                              <span
                                className="inline-flex items-center text-muted-foreground"
                                title="Línea de proveedor — corrígela con Devolver a Pago Proveedores"
                              >
                                <Lock className="h-4 w-4" />
                              </span>
                            ) : (
                              <div className="flex gap-1">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleEditLine(line)}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleDeleteLine(line.id)}
                                >
                                  <Trash2 className="h-4 w-4 text-destructive" />
                                </Button>
                              </div>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    )
                  )}
                  {newLine && (
                    <TableRow>
                      <TableCell>
                        <SearchableSelect
                          value={newLine.account_id}
                          onValueChange={(value) =>
                            setNewLine({ ...newLine, account_id: value })
                          }
                          options={accountOptions}
                          placeholder="Seleccionar cuenta"
                          emptyMessage="Sin cuentas"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          placeholder="Descripción"
                          value={newLine.description || ""}
                          onChange={(e) =>
                            setNewLine({
                              ...newLine,
                              description: e.target.value,
                            })
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="0.00"
                          value={newLine.debit || ""}
                          onChange={(e) =>
                            setNewLine({
                              ...newLine,
                              debit: parseFloat(e.target.value) || 0,
                            })
                          }
                          className="text-right"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="0.00"
                          value={newLine.credit || ""}
                          onChange={(e) =>
                            setNewLine({
                              ...newLine,
                              credit: parseFloat(e.target.value) || 0,
                            })
                          }
                          className="text-right"
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={handleSaveNewLine}
                            disabled={!newLine.account_id}
                          >
                            <CheckCircle className="h-4 w-4 text-green-500" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={handleCancelNewLine}
                          >
                            <XCircle className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                  {lines.length === 0 && !newLine && (
                    <TableRow>
                      <TableCell
                        colSpan={showLineActions ? 5 : 4}
                        className="py-8 text-center text-muted-foreground"
                      >
                        No hay líneas registradas. Agrega una línea para
                        comenzar.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TableCell colSpan={2} className="font-bold">
                      Total
                    </TableCell>
                    <TableCell className="text-right font-mono font-bold">
                      {totalDebit.toLocaleString("es-CL", {
                        minimumFractionDigits: 2,
                      })}
                    </TableCell>
                    <TableCell className="text-right font-mono font-bold">
                      {totalCredit.toLocaleString("es-CL", {
                        minimumFractionDigits: 2,
                      })}
                    </TableCell>
                    {showLineActions && <TableCell />}
                  </TableRow>
                  {!isBalanced && (
                    <TableRow>
                      <TableCell colSpan={2} className="text-destructive">
                        Diferencia
                      </TableCell>
                      <TableCell
                        colSpan={showLineActions ? 3 : 2}
                        className="text-right font-mono text-destructive"
                      >
                        {Math.abs(totalDebit - totalCredit).toLocaleString(
                          "es-CL",
                          {
                            minimumFractionDigits: 2,
                          }
                        )}
                      </TableCell>
                    </TableRow>
                  )}
                </TableFooter>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <MovimientoDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        movement={movement ? { ...movement, lines } : undefined}
        onSubmit={handleUpdate}
        isLoading={updateMutation.isPending}
        lockApLines={isSupplierPaymentDraft}
      />

      {/* Publish Confirmation */}
      <AlertDialog open={publishDialogOpen} onOpenChange={setPublishDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Publicar Comprobante</AlertDialogTitle>
            <AlertDialogDescription>
              Una vez publicado, este comprobante afectará los balances de las
              cuentas involucradas. Esta acción solo puede ser revertida por un
              superadmin.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handlePublish}>
              Publicar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Unpublish Confirmation */}
      <AlertDialog
        open={unpublishDialogOpen}
        onOpenChange={setUnpublishDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Despublicar Comprobante</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción revertirá los efectos de este comprobante en los
              balances de las cuentas. El comprobante volverá a estado de
              borrador.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleUnpublish}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Despublicar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Return to Supplier Payments Confirmation */}
      <AlertDialog open={returnDialogOpen} onOpenChange={setReturnDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              ¿Devolver borrador a Pago Proveedores?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Se revertirá el pago de este comprobante y sus documentos volverán
              a quedar pendientes de pago en Pagos a Proveedor. El borrador se
              eliminará de Comprobantes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleReturnToSupplierPayments}>
              Devolver a Pago Proveedores
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
